import {
  customColumnsControllerArchiveDefinition,
  customColumnsControllerArchivePrecheck,
  customColumnsControllerDeleteDefinition,
  customColumnsControllerListDefinitions,
  customColumnsControllerReplaceDefinitions,
  customColumnsControllerRestoreDefinition,
  tagMigrationControllerRun,
} from '@artic/api-client';
import type {
  ArchiveCustomColumnResponseDto,
  CustomColumnArchivePrecheckResponseDto,
  CustomColumnDefinitionDto,
  TagMigrationEnqueuedResponseDto,
  UpdateCustomColumnDefinitionsDtoDefinitionsItem,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';

export const CUSTOM_COLUMNS_KEY = 'custom-columns' as const;

/**
 * Per-partner custom column definitions (ENG-477).
 *
 * Loaded by the admin config page and by the Schedule editor (which
 * splices a column per definition into the AG Grid). The list is partner-
 * scoped, ordered by display_order ASC, and small (max 50 rows) so a
 * single fetch is cheap.
 */
export function customColumnDefinitionsQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [CUSTOM_COLUMNS_KEY, 'definitions', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await customColumnsControllerListDefinitions(partnerId, { signal });
      return expectData<CustomColumnDefinitionDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useReplaceCustomColumnDefinitions(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (definitions: UpdateCustomColumnDefinitionsDtoDefinitionsItem[]) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await customColumnsControllerReplaceDefinitions(partnerId, { definitions });
      return expectData<CustomColumnDefinitionDto[]>(res);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([CUSTOM_COLUMNS_KEY, 'definitions', partnerId], data);
    },
  });
}

/**
 * Reactivate an archived custom column. The backend rejects with 409 (surfaced
 * as an {@link ApiError} carrying the server message) when an active column
 * already uses the same key. Both restore and permanent-delete return the full
 * updated definitions list, which we write straight into the cache.
 */
export function useRestoreCustomColumnDefinition(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await customColumnsControllerRestoreDefinition(partnerId, definitionId);
      return expectData<CustomColumnDefinitionDto[]>(res);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([CUSTOM_COLUMNS_KEY, 'definitions', partnerId], data);
    },
    // A 4xx here usually means the cached list disagrees with the server (e.g.
    // the column was already restored/deleted in another tab, or a dev-time DB
    // reset). Refetch so the UI converges to server truth instead of leaving a
    // phantom archived row the user can't act on.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: [CUSTOM_COLUMNS_KEY, 'definitions', partnerId],
      });
    },
  });
}

/**
 * ENG-575 — work-order usage pre-check for archiving a custom column. Returns
 * per-status counts plus which confirmation modal to show (`standard` | `blocked`).
 * staleTime 0 so the modal always reflects current usage on open.
 */
export function customColumnArchivePrecheckQueryOptions(
  partnerId: string | undefined,
  definitionId: string | undefined,
) {
  return queryOptions({
    queryKey: [CUSTOM_COLUMNS_KEY, 'archive-precheck', partnerId, definitionId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId || !definitionId) throw new Error('partnerId and definitionId are required');
      const res = await customColumnsControllerArchivePrecheck(partnerId, definitionId, { signal });
      return expectData<CustomColumnArchivePrecheckResponseDto>(res);
    },
    enabled: !!partnerId && !!definitionId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Archive a custom column (ENG-575). `archive_from_future` is the blocked-modal
 * path (In Progress work orders exist → queue + auto-hide); `archive` hides
 * immediately. Invalidates the definitions list so the archived/queued state and
 * the planner columns refresh.
 */
export function useArchiveCustomColumn(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      definitionId: string;
      mode?: 'archive' | 'archive_from_future';
    }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await customColumnsControllerArchiveDefinition(partnerId, params.definitionId, {
        mode: params.mode ?? 'archive',
      });
      return expectData<ArchiveCustomColumnResponseDto>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CUSTOM_COLUMNS_KEY, 'definitions', partnerId],
      });
    },
  });
}

/** Permanently delete an archived custom column (active columns are rejected). */
export function useDeleteCustomColumnDefinition(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await customColumnsControllerDeleteDefinition(partnerId, definitionId);
      return expectData<CustomColumnDefinitionDto[]>(res);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([CUSTOM_COLUMNS_KEY, 'definitions', partnerId], data);
    },
    // See restore: on failure, resync from the server so a stale cache can't
    // strand an archived row in the UI.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: [CUSTOM_COLUMNS_KEY, 'definitions', partnerId],
      });
    },
  });
}

/**
 * Enqueue the background "backfill custom columns from legacy tags" job for the
 * whole partner (the "1st run"). Push-only on the server, so it's safe to re-run;
 * no cache to invalidate (values land on work orders async).
 *
 * The 202 does NOT mean work was queued. Tag→column mappings are hardcoded per
 * partner_key, so the button is a legitimate no-op for any unmapped partner —
 * read `enqueued` / `reason` and report it via {@link describeBackfillOutcome}
 * rather than assuming success.
 */
export function useBackfillCustomColumnsFromTags(partnerId: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await tagMigrationControllerRun(partnerId);
      return expectData<TagMigrationEnqueuedResponseDto>(res);
    },
  });
}

/**
 * Turn a backfill trigger response into the message to show the user, and whether
 * it should read as a success or a warning.
 */
export function describeBackfillOutcome(result: TagMigrationEnqueuedResponseDto): {
  ok: boolean;
  message: string;
} {
  if (!result.enqueued) {
    if (result.reason === 'partner_not_found') {
      return { ok: false, message: 'Partner not found — nothing to backfill.' };
    }
    return {
      ok: false,
      message:
        `No legacy tag mapping is configured for ${result.partner_key ?? 'this partner'} — ` +
        'nothing to backfill.',
    };
  }

  // One prefix for both branches. A coalesced trigger joined a run that is
  // already in flight, and that stays true whether or not every mapping is ready
  // — so it must not be described as newly queued.
  const status = result.reason === 'coalesced' ? 'Backfill already running' : 'Backfill queued';

  const notReady = result.mappings.filter((m) => !m.column_ready);
  if (notReady.length > 0) {
    const detail = notReady
      .map(
        (m) =>
          `“${m.tag_category}” → ${m.metadata_key} (${
            m.column_issue === 'wrong_type' ? 'column is the wrong type' : 'column not created yet'
          })`,
      )
      .join('; ');
    return {
      ok: false,
      message: `${status}, but ${notReady.length} of ${result.mappings.length} mapping(s) will be skipped: ${detail}`,
    };
  }

  const tags = result.mappings.map((m) => m.tag_category).join(', ');
  return {
    ok: true,
    message: `${status} for ${result.mappings.length} mapping(s) (${tags}). Values populate in the background.`,
  };
}
